import CheckIcon from '@mui/icons-material/Check'
import { Box, Button, Divider, Stack, Typography } from '@mui/material'
import { addDays, endOfWeek, startOfWeek } from 'date-fns'
import format from 'date-fns/format'
import { useMemo } from 'react'

import { useBookingContext } from '@providers/booking'
import { TGroomArrivalTime } from '@providers/booking/types'
import { i18WithParams as t } from '@shared/locale'
import { isEmpty } from 'components-ui/src/shared/common'
import { hasRequestTime, hasShowPreferredProvider } from 'components-ui/src/shared/location'
import { ProviderAdminDto } from 'service-api/src/types/api'
import { TPetOffers } from '../types'
import { getInStayLBC, isDayAvailable } from './helper'
import { ShowAnyGroomer } from './show_anygroomer'

export const DatesViewer = ({
  maxDate,
  minDate,
  groomOffer,
  startDate,
  checkProvider,
}: {
  maxDate: Date
  minDate: Date
  groomOffer: TPetOffers
  startDate: Date
  checkProvider: boolean
}) => {
  const bookingCtx = useBookingContext()
  const dateSelected = groomOffer.requestDates[0]?.date
  const inStayLBC = getInStayLBC(bookingCtx.state, 'grooming')
  const mustRequestTime = hasRequestTime(inStayLBC)
  const timeSelected = groomOffer.requestDates[0]?.arrivalTime?.slotStartTime
  const canSelectProvider = hasShowPreferredProvider(inStayLBC)

  const weekStart = startOfWeek(startDate)
  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 0 })
  const bookingInRange = useMemo(
    () => minDate >= weekStart && maxDate <= weekEnd,
    [maxDate, minDate, weekEnd, weekStart]
  )
  const isDataAvailable = useMemo(() => {
    if (isEmpty(bookingCtx.state.data.groomAvailability)) {
      return false
    }
    return !!bookingCtx.state.data.groomAvailability?.[groomOffer.pet.id || 0]
  }, [bookingCtx.state.data.groomAvailability, groomOffer.pet.id])

  const isWeekAvailable = useMemo(() => {
    if (!bookingInRange) {
      return true
    }
    let day = weekStart
    while (day <= weekEnd) {
      if (isDayAvailable(day, groomOffer, bookingCtx.state, weekStart, weekEnd, checkProvider)) {
        return true
      }
      day = addDays(day, 1)
    }

    return false
  }, [bookingCtx.state, bookingInRange, checkProvider, groomOffer, weekEnd, weekStart])

  const onRemove = () => {
    if (dateSelected) {
      bookingCtx.dispatch({
        type: 'SET_REQUEST_DATE',
        payload: {
          groupIdx: groomOffer.groupIdx,
          petIdx: groomOffer.pet.petIdx,
        },
      })
    }
  }

  const stackStyles = dateSelected
    ? { alignItems: 'start' }
    : {
        alignItems: 'center',
        justifyContent: 'center',
      }

  const handleRemoveGrooming = () => {
    const { petIdx } = groomOffer.pet
    const { groupIdx } = groomOffer
    const groom = bookingCtx.state.cart.groups[groupIdx].grooms[petIdx]
    if (groom.offers?.length > 0) {
      bookingCtx.dispatch({
        type: 'REMOVE_GROOM_FOR_PET',
        payload: { groupIdx: groupIdx, petIdx: petIdx },
      })
      onRemove()
    }
  }

  const handleChangeGroomer = () => {
    const { groupIdx } = groomOffer
    const { petIdx } = groomOffer.pet

    bookingCtx.dispatch({
      type: 'SET_GROOM_WIZARD_STATE',
      payload: {
        groupIdx: groupIdx,
        petIdx: petIdx,
        state: 'groomer',
        isDone: false,
        anyGroomer: false,
      },
    })
    bookingCtx.dispatch({
      type: 'SET_REQUEST_DATE',
      payload: {
        groupIdx: groomOffer.groupIdx,
        petIdx: groomOffer.pet.petIdx,
      },
    })
  }

  const onSelectArrivalTime = (groomer: ProviderAdminDto, arrivalTime: TGroomArrivalTime) => {
    const { groupIdx } = groomOffer
    const { petIdx } = groomOffer.pet

    bookingCtx.dispatch({
      type: 'SET_GROOM_ARRIVAL_TIME',
      payload: {
        groupIdx: groupIdx,
        petIdx: petIdx,
        value: arrivalTime,
        state: 'dateSelector',
        isDone: true,
        groomer,
      },
    })
  }

  return (
    <Stack sx={{ width: '100%' }}>
      <Stack {...stackStyles} p={2} sx={{ minHeight: '120px' }}>
        {isDataAvailable && !isWeekAvailable && (
          <Typography variant="body1bold">{t('requestDates.grooms.noAvailableDates')}</Typography>
        )}
        {!dateSelected && isWeekAvailable && (
          <Typography sx={{ fontSize: '16px', fontWeight: '600', lineHeight: '21px' }}>
            {t('requestDates.grooms.selectADate')}
          </Typography>
        )}
        {dateSelected && !mustRequestTime && (
          <>
            <Typography variant="body1bold">
              {t('requestDates.grooms.selectedDay', {
                selected: format(dateSelected, 'MMM dd, yyyy'),
              })}
            </Typography>
            <Typography sx={{ marginTop: 2 }}>{t('requestDates.grooms.reviewText')}</Typography>
          </>
        )}
        {dateSelected && mustRequestTime && (
          <Box width="100%">
            <ShowAnyGroomer groomOffer={groomOffer} onSelect={onSelectArrivalTime} dateSelected={dateSelected} />
          </Box>
        )}
      </Stack>
      <Divider />
      <Stack direction="row" justifyContent="space-between" sx={{ minHeight: 42 }} alignItems="center">
        {(dateSelected && !mustRequestTime) || (dateSelected && timeSelected) ? (
          <Stack direction="row" alignItems="center" gap={1}>
            <CheckIcon fontSize="small" />
            <Typography variant="body2bold">
              {t('groomingWizard.selectionComplete', { petName: groomOffer.pet.name })}
            </Typography>
          </Stack>
        ) : canSelectProvider ? (
          <Button size="small" onClick={handleChangeGroomer}>
            {t('requestDates.grooms.changeGroomer')}
          </Button>
        ) : (
          <div>&nbsp;</div>
        )}
        {dateSelected ? (
          <Button size="small" onClick={onRemove}>
            {t('requestDates.grooms.undoSelection')}
          </Button>
        ) : (
          <Button size="small" onClick={handleRemoveGrooming}>
            {t('requestDates.grooms.removeGroom')}
          </Button>
        )}
      </Stack>
    </Stack>
  )
}
